import { produce } from 'immer';
import type {
    InsightBotInitResponse,
    MerchantInfoSuccessResponseInterface,
    ChatHistoryListSuccessResponseInterface,
    ChatHistorySessionInterface,
    ConvMessage,
} from '../../../interface/ReportsChatbotInterface';
import {
    FETCH_MERCHANT_INFO_REQUEST,
    FETCH_MERCHANT_INFO_SUCCESS,
    FETCH_MERCHANT_INFO_FAILURE,
    START_SESSION_REQUEST,
    START_SESSION_SUCCESS,
    START_SESSION_FAILURE,
    FETCH_CHAT_HISTORY_REQUEST,
    FETCH_CHAT_HISTORY_SUCCESS,
    FETCH_CHAT_HISTORY_FAILURE,
    RESUME_SESSION_REQUEST,
    RESUME_SESSION_SUCCESS,
    RESUME_SESSION_FAILURE,
    CLASSIFY_LOADING_SET,
    METRICS_LOADING_SET,
    INSIGHTS_LOADING_SET,
    CHATBOT_ADD_MESSAGE,
    CHATBOT_UPDATE_MESSAGE,
    CHATBOT_RESET_CONVERSATION,
    CHATBOT_CANCEL_LOADING_MESSAGES,
} from './ReportsChatbotConstants';

interface ReportsChatbotState {
    merchantInfo: MerchantInfoSuccessResponseInterface | null;
    merchantInfoLoading: boolean;
    merchantInfoFailure: boolean;

    sessionData: InsightBotInitResponse | null;
    sessionLoading: boolean;
    sessionFailure: boolean;

    chatHistoryData: ChatHistoryListSuccessResponseInterface | null;
    chatHistoryLoading: boolean;
    chatHistoryFailure: boolean;

    resumeSessionData: ChatHistorySessionInterface | null;
    resumeLoading: boolean;
    resumeFailure: boolean;

    classifyLoading: boolean;
    metricsLoading: boolean;
    insightsLoading: boolean;

    conversation: ConvMessage[];
    // Messages sent while a "Recent Insights" resumed session is open — kept separate
    // from `conversation` so they never leak into the main chat panel.
    resumeLiveMessages: ConvMessage[];
}

const initialState: ReportsChatbotState = {
    merchantInfo: null,
    merchantInfoLoading: false,
    merchantInfoFailure: false,

    sessionData: null,
    sessionLoading: false,
    sessionFailure: false,

    chatHistoryData: null,
    chatHistoryLoading: false,
    chatHistoryFailure: false,

    resumeSessionData: null,
    resumeLoading: false,
    resumeFailure: false,

    classifyLoading: false,
    metricsLoading: false,
    insightsLoading: false,

    conversation: [],
    resumeLiveMessages: [],
};

export default function ReportsChatbotReducer(state = initialState, action: any) {
    return produce(state, (draft) => {
        switch (action.type) {
            case FETCH_MERCHANT_INFO_REQUEST:
                draft.merchantInfoLoading = true;
                draft.merchantInfo = null;
                draft.merchantInfoFailure = false;
                break;
            case FETCH_MERCHANT_INFO_SUCCESS:
                draft.merchantInfoLoading = false;
                draft.merchantInfo = action.payload;
                draft.merchantInfoFailure = false;
                break;
            case FETCH_MERCHANT_INFO_FAILURE:
                draft.merchantInfoLoading = false;
                draft.merchantInfo = null;
                draft.merchantInfoFailure = true;
                break;

            case START_SESSION_REQUEST:
                draft.sessionLoading = true;
                draft.sessionData = null;
                draft.sessionFailure = false;
                // A fresh session bootstrap invalidates any previously resumed history session
                draft.resumeSessionData = null;
                draft.resumeFailure = false;
                draft.resumeLiveMessages = [];
                break;
            case START_SESSION_SUCCESS:
                draft.sessionLoading = false;
                draft.sessionData = action.payload;
                draft.sessionFailure = false;
                break;
            case START_SESSION_FAILURE:
                draft.sessionLoading = false;
                draft.sessionData = null;
                draft.sessionFailure = true;
                break;

            case FETCH_CHAT_HISTORY_REQUEST:
                draft.chatHistoryLoading = true;
                draft.chatHistoryData = null;
                draft.chatHistoryFailure = false;
                break;
            case FETCH_CHAT_HISTORY_SUCCESS:
                draft.chatHistoryLoading = false;
                draft.chatHistoryData = action.payload;
                draft.chatHistoryFailure = false;
                break;
            case FETCH_CHAT_HISTORY_FAILURE:
                draft.chatHistoryLoading = false;
                draft.chatHistoryData = null;
                draft.chatHistoryFailure = true;
                break;

            case RESUME_SESSION_REQUEST:
                draft.resumeLoading = true;
                draft.resumeSessionData = null;
                draft.resumeFailure = false;
                // Entering a (possibly different) history session starts its live tail empty
                draft.resumeLiveMessages = [];
                break;
            case RESUME_SESSION_SUCCESS:
                draft.resumeLoading = false;
                draft.resumeSessionData = action.payload;
                draft.resumeFailure = false;
                // NOTE: intentionally do NOT touch draft.sessionData.session_id here.
                // The main chat's active session must keep its own id; the resumed
                // session's id lives on draft.resumeSessionData.session_id and is used
                // only for messages sent while the "Recent Insights" detail view is open.
                break;
            case RESUME_SESSION_FAILURE:
                draft.resumeLoading = false;
                draft.resumeSessionData = null;
                draft.resumeFailure = true;
                break;

            case CLASSIFY_LOADING_SET:
                draft.classifyLoading = action.payload;
                break;
            case METRICS_LOADING_SET:
                draft.metricsLoading = action.payload;
                break;
            case INSIGHTS_LOADING_SET:
                draft.insightsLoading = action.payload;
                break;

            case CHATBOT_ADD_MESSAGE: {
                const target = action.target === 'resume' ? draft.resumeLiveMessages : draft.conversation;
                target.push(action.payload);
                break;
            }

            case CHATBOT_UPDATE_MESSAGE: {
                const { id, changes } = action.payload;
                const target = action.target === 'resume' ? draft.resumeLiveMessages : draft.conversation;
                const idx = target.findIndex((m) => m.id === id);
                if (idx >= 0) {
                    Object.assign(target[idx], changes);
                }
                break;
            }

            case CHATBOT_RESET_CONVERSATION:
                draft.conversation = [];
                break;

            case CHATBOT_CANCEL_LOADING_MESSAGES:
                draft.classifyLoading = false;
                draft.metricsLoading = false;
                draft.insightsLoading = false;
                [draft.conversation, draft.resumeLiveMessages].forEach((list) => {
                    list.forEach((msg: any) => {
                        if (
                            (msg.type === 'ai-data' || msg.type === 'ai-insights') &&
                            msg.status === 'loading'
                        ) {
                            msg.status = 'skip';
                        }
                    });
                });
                break;

            default:
                return state;
        }
    });
}
